import React, { useEffect, useLayoutEffect, useState, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import {
    MdClose,
    MdExitToApp,
    MdKeyboardArrowDown,
    MdKeyboardArrowUp,
    MdOutlineBookmark,
    MdOutlineBookmarkBorder,
    MdOutlineFlag,
    MdOutlineKeyboard,
    MdOutlineLocationOn,
    MdOutlinePrint
    } from 'react-icons/md';
import { useReactToPrint } from 'react-to-print';
import moment from 'moment';
import Button from 'components/Button2';
import Loader from 'components/Loader';
import axios from 'config/axios';
import { useAuthContext } from 'context/AuthContext';
import useDeviceOS from 'hooks/useDeviceOS';
import { canRequestEdits, buildEditRequestUrl } from 'global/editRequest';
import useOutsideClickHandler from 'hooks/useOutsideClickHandler';
import Shortcuts from './Shortcuts';
import './index.css';

import 'katex/dist/katex.min.css';
import renderMathInElement from "katex/dist/contrib/auto-render.js";
import { flattenKatexUnderlines } from 'global/katexWrap';

const MULTIPLE_CHOICES = ['A', 'B', 'C', 'D', 'E'];
const DISABLE_PRINT = ['A+ Program', 'MT Learning Center', 'PREPARE Test Prep', 'High Peak Education', 'WellEd Labs'];

function TestBCAReview() {
    const [studentName, setStudentName] = useState('');
    const [testModules, setTestModules] = useState(null);
    const [moduleIndex, setModuleIndex] = useState(0);
    const [questionIndex, setQuestionIndex] = useState(0);
    const [moduleSelectOpen, setModuleSelectOpen] = useState(false);
    const [directionsOpen, setDirectionsOpen] = useState(false);
    const [navigatorOpen, setNavigatorOpen] = useState(false);
    const [shortcutsOpen, setShortcutsOpen] = useState(false);
    const [showAnswer, setShowAnswer] = useState(false);
    const [showExplanation, setShowExplanation] = useState(false);
    const [shortcutKeyPressed, setShortcutKeyPressed] = useState(false);
    const [print, setPrint] = useState(false);

    const { user } = useAuthContext();
    const printRef = useRef(null);
    const navigate = useNavigate();
    const { testId } = useParams();
    const moduleSelectRef = useOutsideClickHandler(() => setTimeout(() => setModuleSelectOpen(false), 10));
    const directionsRef = useOutsideClickHandler(() => setTimeout(() => setDirectionsOpen(false), 10));
    const navigatorRef = useOutsideClickHandler(() => setTimeout(() => setNavigatorOpen(false), 10));

    const prevQuestion = () => {
        if (questionIndex > 0) {
            setQuestionIndex(questionIndex - 1);
        }
        else if (moduleIndex > 0) {
            setQuestionIndex(testModules[moduleIndex - 1].questions.length - 1);
            setModuleIndex(moduleIndex - 1);
        }
    }

    const nextQuestion = () => {
        if (questionIndex < testModules[moduleIndex].questions.length - 1) {
            setQuestionIndex(questionIndex + 1);
        }
        else if (moduleIndex < testModules.length - 1) {
            setModuleIndex(moduleIndex + 1);
            setQuestionIndex(0);
        }
        else {
            // exit the review
            navigate(`/report/${testId}`);
        }
    }

    const goToQuestion = (index: number) => {
        setQuestionIndex(index);

        let question = document.getElementById('bcaTestReview__questionChoices');

        if (question) question.scrollTop = 0;
    }

    const formatTime = (time: number) => {
        const duration = moment.duration(time, 'seconds');

        const hours = duration.hours();
        const minutes = (hours && duration.minutes() < 10) ? `0${duration.minutes()}` : duration.minutes();
        const seconds = duration.seconds() < 10 ? `0${duration.seconds()}` : duration.seconds();

        return hours ? `${hours}:${minutes}:${seconds}` : `${minutes}:${seconds}`;
    }

    const renderKatex = () => {
        const katex = document.getElementsByClassName('katex');
        for (let i = 0; i < katex.length; i++) {
            renderMathInElement(katex[i], {
                delimiters: [
                    {left: '$$', right: '$$', display: true},
                    {left: '$', right: '$', display: false},
                    {left: '\\(', right: '\\)', display: false},
                    {left: '\\[', right: '\\]', display: true}
                ],
                trust: true
            });

            katex[i].innerHTML = katex[i].innerHTML.replaceAll('&nbsp;', ' ');
        }

        const wrappingTexts = document.querySelectorAll<HTMLElement>('.katex-html .base');
        for (let i = 0; i < wrappingTexts.length; i++) {
            // console.log(wrappingTexts[i]);
            if (testModules[moduleIndex].subject === 'Reading and Writing') {
                wrappingTexts[i].classList.add('wrap');
            }
            else {
                wrappingTexts[i].classList.remove('wrap');
            }
        }

        flattenKatexUnderlines(document);

        const math = document.querySelectorAll('.katex-mathml');
        for (let i = 0; i < math.length; i++) {
            math[i].remove();
        }
    }

    const handleWindowsShortcutKeys = (event: KeyboardEvent) => {
        const {
            ctrlKey,
            altKey,
            shiftKey,
            code
        } = event;

        if (ctrlKey && altKey && shiftKey) {
            switch(code) {
                case 'KeyD':
                    setDirectionsOpen(!directionsOpen);
                    break;
                default:
                    return;
            }
        }
        else if (ctrlKey && altKey) {
            switch(code) {
                case 'KeyB':
                    prevQuestion();
                    break;
                case 'KeyX':
                    nextQuestion();
                    break;
                // case 'KeyT':
                //     setHideTimer(!hideTimer);
                //     break;
                // case 'KeyV':
                //     markQuestion();
                //     break;
                // case 'KeyA':
                //     if (testModules[moduleIndex].subject === 'Reading and Writing') {
                //         if (annotation) setAnnotation(null);
                //         if (shortcutsOpen) setShortcutsOpen(false);
                //         if (exitOpen) setExitOpen(false);
                //         if (annotation) setAnnotation(null);
                //         else selectAnnotation();
                //     }
                //     break;
                // case 'KeyM':
                //     if (questions[questionIndex].choices.length > 0) {
                //         setCrossOut(!crossOut);
                //     }
                //     break;
                // case 'Digit1':
                //     if (questions[questionIndex].choices.length > 0) {
                //         crossOutChoice(questions[questionIndex].choices[0].id);
                //     }
                //     break;
                // case 'Digit2':
                //     if (questions[questionIndex].choices.length > 0) {
                //         crossOutChoice(questions[questionIndex].choices[1].id);
                //     }
                //     break;
                // case 'Digit3':
                //     if (questions[questionIndex].choices.length > 0) {
                //         crossOutChoice(questions[questionIndex].choices[2].id);
                //     }
                //     break;
                // case 'Digit4':
                //     if (questions[questionIndex].choices.length > 0) {
                //         crossOutChoice(questions[questionIndex].choices[3].id);
                //     }
                //     break;
                default:
                    return;
            }
            
        }
        else if (ctrlKey && shiftKey) {
            switch(code) {
                // case 'Digit1':
                //     if (questions[questionIndex].choices.length > 0) {
                //         handleChange(questions[questionIndex].choices[0].id);
                //     }
                //     break;
                // case 'Digit2':
                //     if (questions[questionIndex].choices.length > 0) {
                //         handleChange(questions[questionIndex].choices[1].id);
                //     }
                //     break;
                // case 'Digit3':
                //     if (questions[questionIndex].choices.length > 0) {
                //         handleChange(questions[questionIndex].choices[2].id);
                //     }
                //     break;
                // case 'Digit4':
                //     if (questions[questionIndex].choices.length > 0) {
                //         handleChange(questions[questionIndex].choices[3].id);
                //     }
                //     break;
                default:
                    return;
            }
        }

        setShortcutKeyPressed(!shortcutKeyPressed);
    }

    const handleMacShortcutKeys = (event: KeyboardEvent) => {
        const {
            ctrlKey,
            altKey,
            shiftKey,
            metaKey,
            code
        } = event;

        if (metaKey && ctrlKey && shiftKey) {
            switch(code) {
                case 'KeyD':
                    setDirectionsOpen(!directionsOpen);
                    break;
                default:
                    return;
            }
        }
        else if (metaKey && ctrlKey) {
            switch(code) {
                case 'KeyB':
                    prevQuestion();
                    break;
                case 'KeyX':
                    nextQuestion();
                    break;
                // case 'Digit3':
                //     if (questions[questionIndex].choices.length > 0) {
                //         handleChange(questions[questionIndex].choices[0].id);
                //     }
                //     break;
                // case 'Digit4':
                //     if (questions[questionIndex].choices.length > 0) {
                //         handleChange(questions[questionIndex].choices[1].id);
                //     }
                //     break;
                // case 'Digit5':
                //     if (questions[questionIndex].choices.length > 0) {
                //         handleChange(questions[questionIndex].choices[2].id);
                //     }
                //     break;
                // case 'Digit6':
                //     if (questions[questionIndex].choices.length > 0) {
                //         handleChange(questions[questionIndex].choices[3].id);
                //     }
                //     break;
                // case 'Digit7':
                //     if (questions[questionIndex].choices.length > 0) {
                //         crossOutChoice(questions[questionIndex].choices[0].id);
                //     }
                //     break;
                // case 'Digit8':
                //     if (questions[questionIndex].choices.length > 0) {
                //         crossOutChoice(questions[questionIndex].choices[1].id);
                //     }
                //     break;
                // case 'Digit9':
                //     if (questions[questionIndex].choices.length > 0) {
                //         crossOutChoice(questions[questionIndex].choices[2].id);
                //     }
                //     break;
                // case 'Digit0':
                //     if (questions[questionIndex].choices.length > 0) {
                //         crossOutChoice(questions[questionIndex].choices[3].id);
                //     }
                //     break;
                default:
                    return;
            }
        }
        else if (metaKey && altKey) {
            switch(code) {
                // case 'KeyG':
                //     setHideTimer(!hideTimer);
                //     break;
                // case 'KeyA':
                //     if (testModules[moduleIndex].subject === 'Reading and Writing') {
                //         if (annotation) setAnnotation(null);
                //         if (shortcutsOpen) setShortcutsOpen(false);
                //         if (exitOpen) setExitOpen(false);
                //         if (annotation) setAnnotation(null);
                //         else selectAnnotation();
                //     }
                //     break;
                // case 'KeyM':
                //     if (questions[questionIndex].choices.length > 0) {
                //         setCrossOut(!crossOut);
                //     }
                //     break;
                default:
                    return;
            }
            
        }
        else if (metaKey && shiftKey) {
            switch(code) {
                // case 'KeyV':
                //     markQuestion();
                //     break;
                default:
                    return;
            }
        }

        setShortcutKeyPressed(!shortcutKeyPressed);
    }

    const dragMove = (element: HTMLElement, header: HTMLElement) => {
        header.style.cursor = 'grab';
        if (!header) {
            return; 
        }
        let pos1 = 0, pos2 = 0, pos3 = 0, pos4 = 0;

        const dragMouseDown = (event) => {
            event.preventDefault();
    
            if (event.type === 'mousedown') {
                pos3 = event.clientX;
                pos4 = event.clientY;
                document.onmousemove = dragElement;
                document.onmouseup = closeDragElement;
            } else if (event.type === 'touchstart') {
                pos3 = event.touches[0].clientX;
                pos4 = event.touches[0].clientY;
                document.ontouchmove = dragElement;
                document.ontouchend = closeDragElement;
            }
        }

        const dragElement = (event) => {
            event.preventDefault();

            if (event.type === 'mousemove') {
                pos1 = pos3 - event.clientX;
                pos2 = pos4 - event.clientY;
                pos3 = event.clientX;
                pos4 = event.clientY;
            } else if (event.type === 'touchmove') {
                pos1 = pos3 - event.touches[0].clientX;
                pos2 = pos4 - event.touches[0].clientY;
                pos3 = event.touches[0].clientX;
                pos4 = event.touches[0].clientY;
            }

            if (pos3 > 0 && pos4 > 0 && pos3 < window.innerWidth && pos4 < window.innerHeight) {
                element.style.top = (element.offsetTop - pos2) + 'px';
                element.style.left = (element.offsetLeft - pos1) + 'px';
            }
            else {
                document.onmouseup(event);
                closeDragElement();
            }
        }

        const closeDragElement = () => {
            document.onmouseup = null;
            document.onmousemove = null;
            document.ontouchend = null;
            document.ontouchmove = null;
        }
    
        header.onmousedown = (event) => {
            if (event.target instanceof Element && (event.target.closest('.digitalReview__popupHeaderIcon') || event.target.closest('.digitalReview__popupHeaderFull'))) {
                return;
            }
            dragMouseDown(event);
        };
    
        header.ontouchstart = (event) => {
            if (event.target instanceof Element && (event.target.closest('.digitalReview__popupHeaderIcon') || event.target.closest('.digitalReview__popupHeaderFull'))) {
                return;
            }
            dragMouseDown(event);
        };
    }

    const handlePrint = useReactToPrint({
        content: () => printRef.current,
        onAfterPrint: () => setPrint(false)
    });

    useEffect(() => {
        axios.get(`tests/digital/bca/tutor/review/${testId}/`)
            .then(res => {
                // console.log(res.data);
                setStudentName(res.data.studentName);
                setTestModules(res.data.testModules);
                // console.log(testId);
            })
            .catch(error => {
                console.log(error);
            });
    }, []);

    useEffect(() => {
        if (testModules) {
            setShortcutKeyPressed(true);

            // dragMove(document.getElementById('shortcutsDrag'), document.getElementById('shortcutsHeader'));
        
            const shortcutsDragEl = document.getElementById('shortcutsDrag');
            const shortcutsHeaderEl = document.getElementById('shortcutsHeader');
            if (shortcutsDragEl && shortcutsHeaderEl) {
                dragMove(shortcutsDragEl, shortcutsHeaderEl);
            }

            const explanationDragEl = document.getElementById('explanationDrag');
            const explanationHeaderEl = document.getElementById('explanationHeader');
            if (explanationDragEl && explanationHeaderEl) {
                dragMove(explanationDragEl, explanationHeaderEl);
            }
        }
    }, [testModules]);

    useLayoutEffect(() => {
        if (testModules) {
            renderKatex();
        }
    }, [testModules, moduleIndex, questionIndex, print]);

    useEffect(() => {
        if (testModules) {
            renderKatex();
        }
    }, [directionsOpen]);

    useEffect(() => {
        if (useDeviceOS() === 'windows') {
            document.addEventListener('keydown', handleWindowsShortcutKeys);
        }
        else if (useDeviceOS() === 'mac_os') {
            document.addEventListener('keydown', handleMacShortcutKeys);
        }

        return () => {
            if (useDeviceOS() === 'windows') {
                document.removeEventListener('keydown', handleWindowsShortcutKeys);
            }
            else if (useDeviceOS() === 'mac_os') {
                document.removeEventListener('keydown', handleMacShortcutKeys);
            }
        }
    }, [shortcutKeyPressed]);

    useEffect(() => {
        if (print) handlePrint();
    }, [print]);

    if (testModules === null) return <Loader />;

    const {
        name,
        questions,
        answers,
        subject,
        prompt
    } = testModules[moduleIndex];

    return (
        <div className='bcaTestReview__box' onCopy={(event) => { event.preventDefault() }}>
            <Helmet>
                <title>
                    Test Review - Test Center Admin
                </title>
            </Helmet>
            {print && <div className='bcaTestReview__print' ref={printRef}>
                {testModules.map(testModule => (<>
                    <h2 className='bcaTestReview__title'>
                        {testModule.name}
                    </h2>
                    {testModule.questions.map((question, q) => (
                        <div className='bcaTestReview__test bcaTestReview__pageBreak'>
                            <div className='bcaTestReview__question'>
                                <div className='bcaTestReview__questionContent'>
                                    <div className='bcaTestReview__questionHeader'>
                                        <p className={testModule.answers[q].isCorrect
                                            ? 'bcaTestReview__questionNumber--correct'
                                            : 'bcaTestReview__questionNumber--wrong'
                                        }>
                                            {q + 1}
                                        </p>
                                        <button className='bcaTestReview__questionMarkButton' onClick={() => {}}>
                                            {testModule.answers[q].marked
                                                ? <MdOutlineBookmark className='bcaTestReview__questionMarkIcon--marked' />
                                                : <MdOutlineBookmarkBorder className='bcaTestReview__questionMarkIcon' />
                                            }
                                            Mark for review
                                        </button>
                                        <button
                                            className={showAnswer
                                                ? 'bcaTestReview__showAnswer--active'
                                                : 'bcaTestReview__showAnswer'
                                            }
                                            onClick={() => setShowAnswer(!showAnswer)}
                                        >
                                            {showAnswer ? 'Hide answer' : 'Show answer'}
                                        </button>
                                    </div>
                                    <div id='bcaTestReview__questionChoices' className='bcaTestReview__questionChoices'>
                                        {question.figures.map(figure => (
                                                <img
                                                    className='bcaTestReview__figure'
                                                    key={figure.id}
                                                    src={figure.image}
                                                    alt={(q + 1).toString()}
                                                />
                                            ))}
                                        <p className='bcaTestReview__questionQuestion'>
                                            <span className='katex'>
                                                {question.question}
                                            </span>
                                        </p>
                                        {question.choices.length
                                        ?   <>
                                                {question.choices.map((choice, i) => (
                                                    <div key={choice.id} className='bcaTestReview__questionChoiceWrapper'>
                                                        <button
                                                            className={showAnswer && choice.isCorrect
                                                                ? 'bcaTestReview__questionChoice--correct'
                                                                : testModule.answers[q].answer == choice.id
                                                                ? (choice.isCorrect 
                                                                    ? 'bcaTestReview__questionChoice--selected'
                                                                    : 'bcaTestReview__questionChoice--wrong'
                                                                )
                                                                : 'bcaTestReview__questionChoice'
                                                            }
                                                            onClick={() => {}}
                                                        >
                                                            <div className='bcaTestReview__questionChoiceOutline'></div>
                                                            <p className='bcaTestReview__questionChoiceLetter'>
                                                                {MULTIPLE_CHOICES[i]}
                                                            </p>
                                                            {choice.figure
                                                            ?   <img
                                                                    className='bcaTestReview__questionChoiceFigure'
                                                                    key={choice.figure}
                                                                    src={choice.figure}
                                                                    alt={choice.figure}
                                                                />
                                                            :   <p className='bcaTestReview__questionChoiceText'>
                                                                    <span className='katex'>
                                                                        {choice.choice}
                                                                    </span>
                                                                </p>
                                                            }
                                                        </button>
                                                    </div>
                                                ))}
                                            </>
                                        :   null
                                        }
                                        <div className='bcaTestReview__marginBottom'></div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    ))}
                </>))}
            </div>}
            <div className='bcaTestReview'>
                <div className='bcaTestReview__header'>
                    <div className='bcaTestReview__titleGroup'>
                        <button className='bcaTestReview__moduleSelect' onClick={() => setModuleSelectOpen(!moduleSelectOpen)}>
                            <h1 className='bcaTestReview__title'>
                                {name}
                            </h1>
                            {moduleSelectOpen
                                ? <MdKeyboardArrowUp className='bcaTestReview__moduleSelectIcon' />
                                : <MdKeyboardArrowDown className='bcaTestReview__moduleSelectIcon' />
                            }
                            {moduleSelectOpen &&
                                <div className='bcaTestReview__moduleSelectDropdown' ref={moduleSelectRef}>
                                    {testModules.map((testModule, t) => (
                                        <p
                                            className={t === moduleIndex
                                                ? 'bcaTestReview__moduleSelectDropdownItem--selected'
                                                : 'bcaTestReview__moduleSelectDropdownItem'
                                            }
                                            onClick={() => {
                                                if (t !== moduleIndex) {
                                                    setModuleIndex(t);
                                                    setQuestionIndex(0);
                                                }
                                            }}
                                        >
                                            {testModule.name}
                                        </p>
                                    ))}
                                </div>
                            }
                        </button>
                        <button className='bcaTestReview__directionsButton' onClick={() => setDirectionsOpen(!directionsOpen)}>
                            {directionsOpen
                                ? <u>Directions</u>
                                : <p>Directions</p>
                            }
                            {directionsOpen
                                ? <MdKeyboardArrowUp className='bcaTestReview__directionsIcon' />
                                : <MdKeyboardArrowDown className='bcaTestReview__directionsIcon' />
                            }
                        </button>
                    </div>
                    <div className='bcaTestReview__timerGroup'>
                        <p className='bcaTestReview__timer'>
                            {formatTime(answers[questionIndex].timeSpent)}
                        </p>
                        <p className='bcaTestReview__timerText'>
                            Time spent
                        </p>
                    </div>
                    <div className='bcaTestReview__actionGroup'>
                        {canRequestEdits(user) && questions[questionIndex].questionId &&
                            <a
                                className='bcaTestReview__action bcaTestReview__editRequest'
                                href={buildEditRequestUrl(questions[questionIndex].questionId, user)}
                                target='_blank'
                                rel='noopener noreferrer'
                            >
                                <MdOutlineFlag className='bcaTestReview__actionIcon' />
                                <p className='bcaTestReview__actionText'>Edit Request</p>
                            </a>
                        }
                        {!(user.organizations.some(organization => DISABLE_PRINT.includes(organization))
                            || user.customizations.find(customization => customization === 'Disable Print')
                        ) && 
                            <button
                                className='bcaTestReview__action'
                                onClick={() => setPrint(true)}
                            >
                                <MdOutlinePrint className='bcaTestReview__actionIcon' />
                                <p className='bcaTestReview__actionText'>Print</p>
                            </button>
                        }
                        <button
                            className={shortcutsOpen
                                ? 'bcaTestReview__action--active'
                                : 'bcaTestReview__action'
                            }
                            onClick={() => {
                                setShortcutsOpen(!shortcutsOpen);
                            }}
                        >
                            <MdOutlineKeyboard className='bcaTestReview__actionIcon' />
                            <p className='bcaTestReview__actionText'>Shortcuts</p>
                        </button>
                        <button
                            className='bcaTestReview__action'
                            onClick={() => navigate(-1)}
                        >
                            <MdExitToApp className='bcaTestReview__actionIcon' />
                            <p className='bcaTestReview__actionText'>Exit</p>
                        </button>
                    </div>
                    {directionsOpen &&
                        <div className='bcaTestReview__directions' ref={directionsRef}>
                            <div className='bcaTestReview__directionsBubble'>
                                <p className='katex bcaTestReview__directionsText'>
                                    {prompt
                                        ? prompt
                                        : 'Lorem ipsum dolor sit amet, consectetur adipiscing elit. Duis sollicitudin, mi congue condimentum dignissim, ipsum diam tristique velit, et rutrum orci massa non purus. Etiam vitae ante sed velit blandit condimentum. Donec lectus ipsum, volutpat vel tortor a, blandit elementum felis. Mauris a ultricies quam, at molestie ipsum. Cras nec ante justo. Suspendisse egestas lectus sit amet sem maximus auctor. In ut dignissim lorem, ac tristique eros. Nullam eget sollicitudin quam. Quisque pellentesque cursus justo sed ornare. Integer finibus, tortor at facilisis molestie, ligula augue cursus ipsum, eget auctor enim sem a eros. Pellentesque habitant morbi tristique senectus et netus et malesuada fames ac turpis egestas. Pellentesque suscipit, nunc eget mollis consequat, augue odio suscipit enim, vel varius eros leo sit amet magna. Nunc suscipit leo et metus bibendum, a finibus libero finibus. In lacinia scelerisque urna ut pretium. In at est aliquam, dictum odio non, fringilla tellus. Vivamus commodo molestie urna at dapibus. Nulla facilisi. Maecenas sit amet lorem tincidunt, maximus nunc eu, malesuada velit. Class aptent taciti sociosqu ad litora torquent per conubia nostra, per inceptos himenaeos. Nulla facilisi.'
                                    }
                                </p>
                                <Button
                                    className='bcaTestReview__directionsClose'
                                    onClick={() => setDirectionsOpen(false)}
                                    secondary
                                >
                                    CLOSE
                                </Button>
                            </div>
                        </div>
                    }
                    <div id='shortcutsDrag' className={shortcutsOpen
                        ? 'bcaTestReview__popup--shortcuts'
                        : 'bcaTestReview__popup--hidden'
                    }>
                        <div id='shortcutsHeader' className='bcaTestReview__popupHeader'>
                            <h3 className='bcaTestReview__popupHeaderText'>
                                Keyboard shortcuts{useDeviceOS() === 'windows'
                                    ? ': Windows'
                                    : useDeviceOS() === 'mac_os'
                                    ? ': MacOS'
                                    : ''
                                }
                            </h3>
                            <MdClose className='bcaTestReview__popupHeaderIcon' onClick={() => setShortcutsOpen(false)} />
                        </div>
                        <Shortcuts />
                    </div>
                    <div id='explanationDrag' className={showExplanation
                        ? 'bcaTestReview__popup--explanation'
                        : 'bcaTestReview__popup--hidden'
                    }>
                        <div id='explanationHeader' className='bcaTestReview__popupHeader'>
                            <h3 className='bcaTestReview__popupHeaderText'>Explanation</h3>
                            <MdClose className='bcaTestReview__popupHeaderIcon' onClick={() => setShowExplanation(false)} />
                        </div>
                        <div className='bcaTestReview__explanationContainer'>
                            {questions[questionIndex].explanationFigure && (
                                <div style={{ textAlign: 'center', marginTop: '20px' }}>
                                    <img
                                        src={questions[questionIndex].explanationFigure}
                                        alt='Explanation Figure'
                                        style={{ maxWidth: questions[questionIndex].explanation ? '80%' : '100%', height: 'auto' }}
                                    />
                                </div>
                            )}
                            <div className='bcaTestReview__explanationBox katex'>
                                {questions[questionIndex].explanation
                                    ? questions[questionIndex].explanation
                                    : questions[questionIndex].explanationFigure
                                    ? ''
                                    : 'No explanation'
                                }
                            </div>
                        </div>
                    </div>
                </div>
                <div className='bcaTestReview__dottedLine'></div>
                <div className='bcaTestReview__test'>
                    <div className='bcaTestReview__question'>
                        <div className='bcaTestReview__questionContent'>
                            <div className='bcaTestReview__questionHeader'>
                                <p className={answers[questionIndex].isCorrect
                                    ? 'bcaTestReview__questionNumber--correct'
                                    : 'bcaTestReview__questionNumber--wrong'
                                }>
                                    {questionIndex + 1}
                                </p>
                                <button className='bcaTestReview__questionMarkButton' onClick={() => {}}>
                                    {answers[questionIndex].marked
                                        ? <MdOutlineBookmark className='bcaTestReview__questionMarkIcon--marked' />
                                        : <MdOutlineBookmarkBorder className='bcaTestReview__questionMarkIcon' />
                                    }
                                    Mark for review
                                </button>
                                {(questions[questionIndex].explanation || questions[questionIndex].explanationFigure) &&
                                    <button
                                        className={showExplanation
                                            ? 'bcaTestReview__explanation--active'
                                            : 'bcaTestReview__explanation'
                                        }
                                        onClick={() => setShowExplanation(!showExplanation)}
                                    >
                                        Explanation
                                    </button>
                                }
                                <button
                                    className={showAnswer
                                        ? 'bcaTestReview__showAnswer--active'
                                        : 'bcaTestReview__showAnswer'
                                    }
                                    onClick={() => setShowAnswer(!showAnswer)}
                                >
                                    {showAnswer ? 'Hide answer' : 'Show answer'}
                                </button>
                            </div>
                            <div id='bcaTestReview__questionChoices' className='bcaTestReview__questionChoices'>
                                {questions[questionIndex].figures.map(figure => (
                                        <img
                                            className='bcaTestReview__figure'
                                            key={figure.id}
                                            src={figure.image}
                                            alt={(questionIndex + 1).toString()}
                                            draggable={false}
                                        />
                                    ))}
                                <p className='bcaTestReview__questionQuestion'>
                                    <span className='katex'>
                                        {questions[questionIndex].question}
                                    </span>
                                </p>
                                {questions[questionIndex].choices.length
                                ?   <>
                                        {questions[questionIndex].choices.map((choice, i) => (
                                            <div key={choice.id} className='bcaTestReview__questionChoiceWrapper'>
                                                <button
                                                    className={showAnswer && choice.isCorrect
                                                        ? 'bcaTestReview__questionChoice--correct'
                                                        : answers[questionIndex].answer == choice.id
                                                        ? (choice.isCorrect 
                                                            ? 'bcaTestReview__questionChoice--selected'
                                                            : 'bcaTestReview__questionChoice--wrong'
                                                        )
                                                        : 'bcaTestReview__questionChoice'
                                                    }
                                                    onClick={() => {}}
                                                >
                                                    <div className='bcaTestReview__questionChoiceOutline'></div>
                                                    <p className='bcaTestReview__questionChoiceLetter'>
                                                        {MULTIPLE_CHOICES[i]}
                                                    </p>
                                                    {choice.figure
                                                    ?   <img
                                                            className='bcaTestReview__questionChoiceFigure'
                                                            key={choice.figure}
                                                            src={choice.figure}
                                                            alt={choice.figure}
                                                            draggable={false}
                                                        />
                                                    :   <span className='bcaTestReview__questionChoiceTextWrapper'>
                                                            <p className='katex bcaTestReview__questionChoiceText'>
                                                                {choice.choice}
                                                            </p>
                                                        </span>
                                                    }
                                                </button>
                                            </div>
                                        ))}
                                    </>
                                :   null
                                }
                                <div className='bcaTestReview__marginBottom'></div>
                            </div>
                        </div>
                    </div>
                </div>
                <div className='bcaTestReview__dottedLine'></div>
                <div className='bcaTestReview__footer'>
                    <p className='bcaTestReview__tester'>
                        {studentName}
                    </p>
                    <button
                        className='bcaTestReview__navigatorToggle'
                        onClick={() => setNavigatorOpen(!navigatorOpen)}
                    >
                        Question {questionIndex + 1}/{questions.length}
                        {navigatorOpen
                            ? <MdKeyboardArrowDown className='bcaTestReview__navigatorToggleIcon' />
                            : <MdKeyboardArrowUp className='bcaTestReview__navigatorToggleIcon' />
                        }
                    </button>
                    <div className='bcaTestReview__buttons'>
                        <Button
                            className='bcaTestReview__button'
                            secondary
                            disabled={moduleIndex === 0 && questionIndex === 0}
                            onClick={prevQuestion}
                        >
                            BACK
                        </Button>
                        <Button
                            className='bcaTestReview__button'
                            onClick={nextQuestion}
                        >
                            NEXT
                        </Button>
                    </div>
                    {navigatorOpen &&
                        <div className='bcaTestReview__navigator' ref={navigatorRef}>
                            <div className='bcaTestReview__navigatorBubble'>
                                <h2 className='bcaTestReview__navigatorTitle'>
                                    {name} Questions
                                </h2>
                                <div className='bcaTestReview__navigatorLegends'>
                                    <div className='bcaTestReview__navigatorLegend'>
                                        <MdOutlineLocationOn className='bcaTestReview__navigatorLegendCurrent' />
                                        <p className='bcaTestReview__navigatorLegendText'>
                                            Current
                                        </p>
                                    </div>
                                    <div className='bcaTestReview__navigatorLegend'>
                                        <div className='bcaTestReview__navigatorLegendCorrect'></div>
                                        <p className='bcaTestReview__navigatorLegendText'>
                                            Correct
                                        </p>
                                    </div>
                                    <div className='bcaTestReview__navigatorLegend'>
                                        <div className='bcaTestReview__navigatorLegendWrong'></div>
                                        <p className='bcaTestReview__navigatorLegendText'>
                                            Incorrect
                                        </p>
                                    </div>
                                    <div className='bcaTestReview__navigatorLegend'>
                                        <MdOutlineBookmark className='bcaTestReview__navigatorLegendMarked' />
                                        <p className='bcaTestReview__navigatorLegendText'>
                                            For review
                                        </p>
                                    </div>
                                </div>
                                <div className='bcaTestReview__navigatorItems'>
                                    {questions.map((question, i) => (
                                        <button
                                            key={`${question.id}nav`}
                                            className={answers[i].isCorrect
                                                ? 'bcaTestReview__navigatorItem--correct'
                                                : 'bcaTestReview__navigatorItem--wrong'
                                            }
                                            onClick={() => {
                                                goToQuestion(i);
                                                setNavigatorOpen(false);
                                            }}
                                        >
                                            {questionIndex === i
                                                ? <u className='bcaTestReview__navigatorItemNumber'>{i + 1}</u>
                                                : <p className='bcaTestReview__navigatorItemNumber'>{i + 1}</p>
                                            }
                                            {questionIndex === i && <MdOutlineLocationOn className='bcaTestReview__navigatorItemCurrent' />}
                                            {answers[i].marked && <MdOutlineBookmark className='bcaTestReview__navigatorItemMarked' />}
                                        </button>
                                    ))}
                                </div>
                            </div>
                        </div>
                    }
                </div>
            </div>
        </div>
    );
}

export default TestBCAReview;